import React from 'react';
import { cn } from '../utils/cn';

export function ILMSFormSection({
  title,
  description,
  children,
  columns = 2,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
  columns?: 1 | 2 | 3;
}) {
  const colClass = columns === 1 ? 'sm:grid-cols-1' : columns === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2';
  return (
    <fieldset className="border-0 p-0 m-0">
      <div className="mb-3">
        <legend className="text-section-title">{title}</legend>
        {description && <p className="text-secondary text-ink-500 mt-0.5">{description}</p>}
      </div>
      <div className={cn('grid grid-cols-1 gap-x-4 gap-y-4', colClass)}>{children}</div>
    </fieldset>
  );
}

export interface ILMSFormFieldProps {
  label: string;
  htmlFor?: string;
  required?: boolean;
  optional?: boolean;
  autoCalculated?: boolean;
  systemGenerated?: boolean;
  error?: string;
  helperText?: string;
  /** A small inline action below the field, e.g. "+ Add new animal" linking
   * out to where that's actually done, for a field whose options can't
   * cover every case yet. */
  action?: { label: string; onClick: () => void };
  children: React.ReactNode;
  className?: string;
  fullWidth?: boolean;
}

/** Wraps one input with a consistent label + required/optional/auto-calculated/system-generated tag + error/helper text. */
export function ILMSFormField({
  label,
  htmlFor,
  required,
  optional,
  autoCalculated,
  systemGenerated,
  error,
  helperText,
  action,
  children,
  className,
  fullWidth,
}: ILMSFormFieldProps) {
  return (
    <div className={cn('flex flex-col gap-1', fullWidth && 'sm:col-span-full', className)}>
      <label htmlFor={htmlFor} className="text-label text-ink-700 flex items-center gap-1.5 flex-wrap">
        {label}
        {required && <span className="text-critical-600">*</span>}
        {optional && <span className="text-helper-text text-ink-400 font-normal">(optional)</span>}
        {autoCalculated && (
          <span className="text-helper-text font-normal text-info-700 bg-info-50 border border-info-100 rounded px-1.5 py-0.5">
            Auto-calculated
          </span>
        )}
        {systemGenerated && (
          <span className="text-helper-text font-normal text-ink-500 bg-surface-muted border border-ink-300/30 rounded px-1.5 py-0.5">
            System generated
          </span>
        )}
      </label>
      {children}
      {error && <p className="text-error-text text-critical-600">{error}</p>}
      {!error && helperText && <p className="text-helper-text text-ink-400">{helperText}</p>}
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          className="text-helper-text text-brand-green-700 hover:underline self-start"
        >
          {action.label}
        </button>
      )}
    </div>
  );
}

export const ilmsInputClass = (hasError?: boolean) =>
  cn(
    'h-11 min-h-touch w-full rounded-md border px-3 text-body bg-white',
    'focus-visible:outline-2 focus-visible:outline-brand-green-500',
    'disabled:bg-surface-muted disabled:text-ink-400',
    hasError ? 'border-critical-500' : 'border-ink-300/50'
  );

export const ILMSInput = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement> & { error?: boolean }>(
  function ILMSInput({ className, error, ...rest }, ref) {
    return <input ref={ref} className={cn(ilmsInputClass(error), className)} {...rest} />;
  }
);

export const ILMSTextarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement> & { error?: boolean }>(
  function ILMSTextarea({ className, error, ...rest }, ref) {
    return <textarea ref={ref} className={cn(ilmsInputClass(error), 'h-auto min-h-[5.5rem] py-2', className)} {...rest} />;
  }
);

export const ILMSSelect = React.forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement> & { error?: boolean }>(
  function ILMSSelect({ className, error, children, ...rest }, ref) {
    return (
      <select ref={ref} className={cn(ilmsInputClass(error), 'pr-8', className)} {...rest}>
        {children}
      </select>
    );
  }
);
